import { el, mount, unmount, setAttr } from 'redom';

import { BehaviorSubject } from 'rxjs';
import { map, distinctUntilChanged } from 'rxjs/operators';

import HelptapColorPicker from 'generics/components/color-picker-native';
import RangeInput from 'generics/components/font-size-input';
import { stringToSVG } from 'utilities';

import FontFamilySelector from './font-family-selector';
import FontFamilyButtonSection from './font-family-button';
import FontWeightSelector from './font-weight-selector';
import ShadowInputPanel from './shadow-input-panel';
import PositionControlPanel from './position-control-panel';
import DuplicateObject from './duplicate-object';

import ToolbarAnchor from './anchor';

import actionTypes from './action-types';

import './index.scss';

import toolbarTextIconString from 'assets/images/text-color-icon.svg?raw';

import ToolbarBackgroundColorIconString from 'assets/images/text-background-color-icon.svg?raw';

import FontWeightIconString from 'assets/images/font-weight-icon.svg?raw';
import LineHeightIconString from 'assets/images/line-height-icon.svg?raw';
import FontSizeIconString from 'assets/images/font-size-icon.svg?raw';
import boxShadowIconString from 'assets/images/box-shadow-icon.svg?raw';

const boxShadowIcon = stringToSVG(boxShadowIconString);
const FontSizeIcon = stringToSVG(FontSizeIconString);
const LineHeightIcon = stringToSVG(LineHeightIconString);
const FontWeightIcon = stringToSVG(FontWeightIconString);

const ToolbarBackgroundColorIcon = stringToSVG(ToolbarBackgroundColorIconString);

const ToolbarTextColorIcon = stringToSVG(toolbarTextIconString);

setAttr(ToolbarTextColorIcon, {
  class: 'font-color-toggle-icon'
});

class CanvasTextToolbar {

  constructor(configuration, scrollableParent) {

    this.configuration = configuration;
    this.scrollableParent = scrollableParent;
    this.state$ = new BehaviorSubject({
      displayTextColorPicker: false,
      displayBackgroundColorPicker: false,
      displayFontFamilySelector: false,
      displayFontWeightSelector: false,
      displayFontSizeSelector: false,
      displayLineHeightSelector: false,
      displayShadowInputPanel: false
    });

    this.displayTextColorPicker$ = this.state$.pipe(map(state => state.displayTextColorPicker), distinctUntilChanged());
    this.displayBackgroundColorPicker$ = this.state$.pipe(map(state => state.displayBackgroundColorPicker), distinctUntilChanged());
    this.displayFontFamilySelector$ = this.state$.pipe(map(state => state.displayFontFamilySelector), distinctUntilChanged());
    this.displayFontWeightSelector$ = this.state$.pipe(map(state => state.displayFontWeightSelector), distinctUntilChanged());
    this.displayFontSizeSelector$ = this.state$.pipe(map(state => state.displayFontSizeSelector), distinctUntilChanged());
    this.displayLineHeightSelector$ = this.state$.pipe(map(state => state.displayLineHeightSelector), distinctUntilChanged());
    this.displayShadowInputPanel$ = this.state$.pipe(map(state => state.displayShadowInputPanel), distinctUntilChanged());

    this.dynamicContentContainer = el('.editor-toolbar-dynamic-content-container');
    this.fontFamilyButtonSection = null;
    this.fontFamilySelector = null;
    this.fontWeightButtonSection = null;
    this.fontWeightSelector = null;
    this.fontSizeButtonSection = null;
    this.fontSizeSelector = null;
    this.lineHeightButtonSection = null;
    this.lineHeightSelector = null;
    this.textColorPicker = null;
    this.backgroundColorPicker = null;
    this.alignActionButtons = null;
    this.shadowInputPanel = null;

    this.el = el('.canvas-text-toolbar-main', [
      this.renderToolbar(),
      this.renderDynamicContent()
    ]);

    this.displayTextColorPicker$.subscribe(this.handleTextColorPickerDisplayChange.bind(this));
    this.displayBackgroundColorPicker$.subscribe(this.handleBackgroundColorPickerDisplayChange.bind(this));
    this.displayFontFamilySelector$.subscribe(this.handleFontFamilySelectorDisplayChange.bind(this));
    this.displayFontWeightSelector$.subscribe(this.handleFontWeightSelectorDisplayChange.bind(this));
    this.displayFontSizeSelector$.subscribe(this.handleFontSizeSelectorDisplayChange.bind(this));
    this.displayLineHeightSelector$.subscribe(this.handleLineHeightSelectorDisplayChange.bind(this));
    this.displayShadowInputPanel$.subscribe(this.handleShadowInputPanelDisplayChange.bind(this));

    this.displayTextColorPicker$.subscribe(this.toggleActiveStatus.bind(this));
    this.displayBackgroundColorPicker$.subscribe(this.toggleActiveStatus.bind(this));
    this.displayFontFamilySelector$.subscribe(this.toggleActiveStatus.bind(this));
    this.displayFontWeightSelector$.subscribe(this.toggleActiveStatus.bind(this));
    this.displayFontSizeSelector$.subscribe(this.toggleActiveStatus.bind(this));
    this.displayLineHeightSelector$.subscribe(this.toggleActiveStatus.bind(this));
    this.displayShadowInputPanel$.subscribe(this.toggleActiveStatus.bind(this));

  }

  toggleActiveStatus(isVisible) {

    if (isVisible === true) {
      this.configuration.onActive();
    } else {
      this.configuration.onInactive();
    }

  }

  updateState(newState) {
    this.state$.next({ ...this.state$.value, ...newState });
  }

  handleShadowChange(shadow) {

    this.configuration.shadow = shadow;

    if (typeof this.configuration.callbacks.onShadowChange === 'function') {
      this.configuration.callbacks.onShadowChange(shadow);
    }

  }

  handleShadowInputPanelDisplayChange(isVisible) {

    if (isVisible === true) {
      this.shadowInputPanel = new ShadowInputPanel({
        value: this.configuration.shadow,
        onChange: (shadow) => this.handleShadowChange(shadow),
        onClose: () => this.updateState({
          displayShadowInputPanel: !this.state$.value.displayShadowInputPanel
        })
      });
      mount(this.dynamicContentContainer, this.shadowInputPanel);
    } else if (this.shadowInputPanel !== null) {
      unmount(this.dynamicContentContainer, this.shadowInputPanel);
      this.shadowInputPanel = null;
    }

  }

  handleLineHeightChange(lineHeight) {

    this.configuration.lineHeight = lineHeight;

    if (typeof this.configuration.callbacks.onLineHeightChange === 'function') {
      this.configuration.callbacks.onLineHeightChange(lineHeight);
    }

  }

  handleLineHeightSelectorDisplayChange(isVisible) {

    if (isVisible === true) {
      this.lineHeightSelector = new RangeInput({
        size: this.configuration.lineHeight || 22.5,
        min: 8,
        max: 100,
        step: .5,
        onChange: (size) => this.handleLineHeightChange(size),
        onClose: () => this.updateState({ displayLineHeightSelector: !this.state$.value.displayLineHeightSelector })
      });
      mount(this.lineHeightButtonSection, this.lineHeightSelector);
    } else if (this.lineHeightSelector !== null) {
      unmount(this.lineHeightButtonSection, this.lineHeightSelector);
      this.lineHeightSelector = null;
    }

  }

  handleFontSizeChange(fontSize) {

    this.configuration.font.size = fontSize;

    if (typeof this.configuration.callbacks.onFontSizeChange === 'function') {
      this.configuration.callbacks.onFontSizeChange(fontSize);
    }

  }

  handleFontSizeSelectorDisplayChange(isVisible) {

    if (isVisible === true) {
      this.fontSizeSelector = new RangeInput({
        size: this.configuration.font.size,
        min: 8,
        max: 100,
        step: .5,
        onChange: (size) => this.handleFontSizeChange(size),
        onClose: () => this.updateState({ displayFontSizeSelector: !this.state$.value.displayFontSizeSelector })
      });
      mount(this.fontSizeButtonSection, this.fontSizeSelector);
    } else if (this.fontSizeSelector !== null) {
      unmount(this.fontSizeButtonSection, this.fontSizeSelector);
      this.fontSizeSelector = null;
    }

  }

  handleFontWeightChange(values) {

    const fontWeight = values[0].key;

    this.configuration.font.weight = fontWeight;

    if (typeof this.configuration.callbacks.onFontWeightChange === 'function') {
      this.configuration.callbacks.onFontWeightChange(fontWeight);
    }

  }

  handleFontWeightSelectorDisplayChange(isVisible) {

    if (isVisible === true) {
      this.fontWeightSelector = new FontWeightSelector({
        fontWeight: this.configuration.font.weight,
        onChange: (fontWeight) => this.handleFontWeightChange(fontWeight),
        onClose: () => this.updateState({ displayFontWeightSelector: !this.state$.value.displayFontWeightSelector })
      });
      mount(this.fontWeightButtonSection, this.fontWeightSelector);
    } else if (this.fontWeightSelector !== null) {
      unmount(this.fontWeightButtonSection, this.fontWeightSelector);
      this.fontWeightSelector = null;
    }

  }

  handleFontFamilyChange(value) {

    const fontFamily = value[0].key;

    this.configuration.font.family = fontFamily;

    this.fontFamilyButtonSection.update(fontFamily);

    if (typeof this.configuration.callbacks.onFontFamilyChange === 'function') {
      this.configuration.callbacks.onFontFamilyChange(fontFamily, value[0]);
    }

  }

  handleFontFamilySelectorDisplayChange(isVisible) {

    if (isVisible === true) {
      this.fontFamilySelector = new FontFamilySelector({
        fontFamily: this.configuration.font.family,
        onChange: (fontFamily) => this.handleFontFamilyChange(fontFamily),
        onClose: () => this.updateState({ displayFontFamilySelector: !this.state$.value.displayFontFamilySelector })
      });
      mount(this.fontFamilyButtonSection, this.fontFamilySelector);
    } else if (this.fontFamilySelector !== null) {
      unmount(this.fontFamilyButtonSection, this.fontFamilySelector);
      this.fontFamilySelector = null;
    }

  }

  handleTextColorChange(color) {

    this.configuration.color.text = color;

    if (typeof this.configuration.callbacks.onTextColorChange === 'function') {
      this.configuration.callbacks.onTextColorChange(color);
    }

  }

  handleTextColorPickerDisplayChange(isVisible) {

    if (isVisible === true) {
      this.textColorPicker = new HelptapColorPicker({
        color: this.configuration.color.text,
        onChange: (color) => this.handleTextColorChange(color),
        onClose: () => this.updateState({ displayTextColorPicker: !this.state$.value.displayTextColorPicker }),
        position: 'top',
        autoPosition: true,
        scrollableParent: this.scrollableParent
      });
      mount(this.dynamicContentContainer, this.textColorPicker);
    } else if (this.textColorPicker !== null) {
      unmount(this.dynamicContentContainer, this.textColorPicker);
      this.textColorPicker = null;
    }

  }

  handleBackgroundColorChange(color) {

    this.configuration.color.background = color;

    if (typeof this.configuration.callbacks.onBackgroundColorChange === 'function') {
      this.configuration.callbacks.onBackgroundColorChange(color);
    }

  }

  handleBackgroundColorPickerDisplayChange(isVisible) {

    if (isVisible === true) {
      this.backgroundColorPicker = new HelptapColorPicker({
        color: this.configuration.color.background,
        onChange: (color) => this.handleBackgroundColorChange(color),
        onClose: () => this.updateState({ displayBackgroundColorPicker: !this.state$.value.displayBackgroundColorPicker }),
        position: 'top',
        autoPosition: true,
        scrollableParent: this.scrollableParent
      });
      mount(this.dynamicContentContainer, this.backgroundColorPicker);
    } else if (this.backgroundColorPicker !== null) {
      unmount(this.dynamicContentContainer, this.backgroundColorPicker);
      this.backgroundColorPicker = null;
    }

  }

  handleBoldClick() {

    const bold = this.configuration.marks.bold === false;

    this.configuration.marks.bold = bold;

    if (typeof this.configuration.callbacks.onBoldChange === 'function') {
      this.configuration.callbacks.onBoldChange(bold);
    }

  }

  handleItalicClick() {

    const italic = this.configuration.marks.italic === false;

    this.configuration.marks.italic = italic;

    if (typeof this.configuration.callbacks.onItalicChange === 'function') {
      this.configuration.callbacks.onItalicChange(italic);
    }

  }

  handleUnderlineClick() {

    const underline = this.configuration.marks.underline === false;

    this.configuration.marks.underline = underline;

    if (typeof this.configuration.callbacks.onUnderlineChange === 'function') {
      this.configuration.callbacks.onUnderlineChange(underline);
    }

  }

  handleStrikeThroughClick() {

    const strikeThrough = this.configuration.marks.strikeThrough === false;

    this.configuration.marks.strikeThrough = strikeThrough;

    if (typeof this.configuration.callbacks.onStrikeThroughChange === 'function') {
      this.configuration.callbacks.onStrikeThroughChange(strikeThrough);
    }

  }

  normalizeTextAlignment(currentSide, currentState) {

    this.configuration.alignment.left = false;
    this.configuration.alignment.center = false;
    this.configuration.alignment.right = false;

    if (currentState === true) {
      this.configuration.alignment[currentSide] = true;
    } else if (currentSide !== 'center') {
      this.configuration.alignment.center = true;
    }

  }

  handleAlignLeftClick() {

    const value = this.configuration.alignment.left === false;

    this.normalizeTextAlignment('left', value);

    if (typeof this.configuration.callbacks.onAlignmentLeftChange === 'function') {
      this.configuration.callbacks.onAlignmentLeftChange(value);
    }

  }

  handleAlignCenterClick() {

    const value = this.configuration.alignment.center === false;

    this.normalizeTextAlignment('center', value);

    if (typeof this.configuration.callbacks.onAlignmentCenterChange === 'function') {
      this.configuration.callbacks.onAlignmentCenterChange(value);
    }

  }

  handleAlignRightClick() {

    const value = this.configuration.alignment.right === false;

    this.normalizeTextAlignment('right', value);

    if (typeof this.configuration.callbacks.onAlignmentRightChange === 'function') {
      this.configuration.callbacks.onAlignmentRightChange(value);
    }

  }

  renderMarkButtonsAnchor() {

    const anchorProperties = {
      dynamicContentContainer: this.dynamicContentContainer,
      marks: [
        { type: actionTypes.BOLD, active: this.configuration.marks.bold, tooltip: 'Bold' },
        { type: actionTypes.ITALIC, active: this.configuration.marks.italic, tooltip: 'Italic' },
        { type: actionTypes.UNDERLINE, active: this.configuration.marks.underline, tooltip: 'Underline' },
        { type: actionTypes.STRIKE_THROUGH, active: this.configuration.marks.strikeThrough, tooltip: 'Strike through' }
      ],
      onClick: (markType) => {
        if (markType === actionTypes.BOLD) {
          this.handleBoldClick();
        } else if (markType === actionTypes.ITALIC) {
          this.handleItalicClick();
        } else if (markType === actionTypes.UNDERLINE) {
          this.handleUnderlineClick();
        } else if (markType === actionTypes.STRIKE_THROUGH) {
          this.handleStrikeThroughClick();
        }
      }
    };

    return new ToolbarAnchor(anchorProperties);

  }

  buildAlignmentAnchorProperties() {

    const anchorProperties = {
      dynamicContentContainer: this.dynamicContentContainer,
      group: 'alignment',
      marks: [
        { type: actionTypes.ALIGN_LEFT, active: this.configuration.alignment.left, tooltip: 'Align left' },
        { type: actionTypes.ALIGN_CENTER, active: this.configuration.alignment.center, tooltip: 'Align center' },
        { type: actionTypes.ALIGN_RIGHT, active: this.configuration.alignment.right, tooltip: 'Align right' }
      ],
      onClick: (markType) => {
        if (markType === actionTypes.ALIGN_LEFT) {
          this.handleAlignLeftClick();
        } else if (markType === actionTypes.ALIGN_CENTER) {
          this.handleAlignCenterClick();
        } else if (markType === actionTypes.ALIGN_RIGHT) {
          this.handleAlignRightClick();
        }
        this.alignActionButtons.update(this.buildAlignmentAnchorProperties());
      }
    };

    return anchorProperties;

  }

  renderAlignmentButtonsSection() {

    const anchorProperties = this.buildAlignmentAnchorProperties();

    this.alignActionButtons = new ToolbarAnchor(anchorProperties);

    return this.alignActionButtons;

  }

  renderColorButtons() {

    return ([
      el('x-tooltip', { text: 'Text color' },
        el('button.inline-toolbar-button.color-button', {
          onclick: () => this.updateState({ displayTextColorPicker: !this.state$.value.displayTextColorPicker })
        }, [ToolbarTextColorIcon.cloneNode(true)])),
      el('x-tooltip', { text: 'Text background color' },
        el('button.inline-toolbar-button.color-button', {
          onclick: () => this.updateState({ displayBackgroundColorPicker: !this.state$.value.displayBackgroundColorPicker })
        }, [ToolbarBackgroundColorIcon.cloneNode(true)])),
      el('x-tooltip', { text: 'Shadow' },
        el('button.inline-toolbar-button.shadow-button', {
          onclick: () => this.updateState({
            displayShadowInputPanel:
              !this.state$.value.displayShadowInputPanel
          })
        }, [boxShadowIcon.cloneNode(true)]))
    ]);

  }

  renderColorButtonsSection() {

    return el('.toolbar-section.color-buttons-section', [
      this.renderColorButtons()
    ]);

  }

  renderFontFamilyButton() {

    this.fontFamilyButtonSection = new FontFamilyButtonSection({
      fontFamily: this.configuration.font.family,
      onToggle: () => this.updateState({ displayFontFamilySelector: !this.state$.value.displayFontFamilySelector })
    });

    return this.fontFamilyButtonSection;

  }

  renderFontWeightButton() {

    this.fontWeightButtonSection = el('.font-weight-button-section', [
      el('x-tooltip', { text: 'Font weight' },
        el('button.inline-toolbar-button.font-weight-button', {
          onclick: () => this.updateState({ displayFontWeightSelector: !this.state$.value.displayFontWeightSelector })
        }, [FontWeightIcon.cloneNode(true)]))
    ]);

    return this.fontWeightButtonSection;

  }

  renderFontSizeButton() {

    this.fontSizeButtonSection = el('.font-size-button-section', [
      el('x-tooltip', { text: 'Font size' },
        el('button.inline-toolbar-button.font-size-button', {
          onclick: () => this.updateState({ displayFontSizeSelector: !this.state$.value.displayFontSizeSelector })
        }, [FontSizeIcon.cloneNode(true)]))
    ]);

    return this.fontSizeButtonSection;

  }

  renderLineHeightButton() {

    this.lineHeightButtonSection = el('.line-height-button-section', [
      el('x-tooltip', { text: 'Line height' },
        el('button.inline-toolbar-button.line-height-button', {
          onclick: () => this.updateState({ displayLineHeightSelector: !this.state$.value.displayLineHeightSelector })
        }, [LineHeightIcon.cloneNode(true)]))
    ]);

    return this.lineHeightButtonSection;

  }

  renderFontButtons() {

    return ([
      this.renderFontFamilyButton(),
      this.renderFontSizeButton(),
      this.renderFontWeightButton(),
      this.renderLineHeightButton()
    ]);

  }

  renderFontButtonsSection() {

    return el('.toolbar-section', [
      this.renderFontButtons()
    ]);

  }

  renderPositionControlPanel() {

    return new PositionControlPanel(this.configuration.callbacks);

  }

  renderToolbar() {

    return el('.toolbar-buttons-container editor-tool-container', [
      el('.toolbar-section', [
        this.renderMarkButtonsAnchor()
      ]),
      this.renderAlignmentButtonsSection(),
      this.renderColorButtonsSection(),
      this.renderFontButtonsSection(),
      this.renderPositionControlPanel(),
      el('.toolbar-section', [
        new DuplicateObject(this.configuration)
      ])
    ]);

  }

  renderDynamicContent() {

    return this.dynamicContentContainer;

  }

}

export default CanvasTextToolbar;